/** @format */

import {ForecastList, ValueKey, ValueList} from '../types';

interface Options {
  // Список дней, для которых нужно вычислить прогноз.
  forecastDays: ValueKey[];
  // Коллекция значений графика.
  values: ValueList;
  // Посчитать как кумулятивный (складывать значения из основного графика, а каждый следующий прогноз делать больше предыдущего).
  isCumulative?: boolean;
}

/**
 * Вычислит прогноз, используя коллекцию со значениями по дням.
 * В результате вернёт коллекцию прогнозов по дням, указанным в forecastDays.
 * ВАЖНО! Значения и прогнозы не должны накладываться друг на друга, а прогноз должен быть позже значений.
 * @param values
 * @param forecastDays
 * @param isCumulative
 */

const calcForecast = ({values, forecastDays, isCumulative}: Options): ForecastList => {
  if (!forecastDays.length) {
    return {};
  }

  const valueADates = Object.keys(values);

  if (!valueADates.length) {
    return {};
  }

  // Значения и дни должны быть сортированы от меньшего к большему

  valueADates.sort();
  const sortedForecastDays = forecastDays.toSorted();

  // Самый ранний день прогноза не может быть меньше или равен самому позднему дню со значением

  if (sortedForecastDays[0] <= valueADates[valueADates.length - 1]) {
    throw Error(
      'Forecast calc error: The earliest forecast day must be greater than the latest value day.',
    );
  }

  // Вычисляем сумму значений

  const valueSum = valueADates.reduce((acc, date) => {
    return acc + values[date];
  }, 0);

  // Общее число дней со значениями

  const valueDays = valueADates.length;

  // Среднее дневное значение

  const averageDayValue = valueSum / valueDays;

  // Собираем коллекцию отклонений

  const diffList = valueADates.reduce<ValueList>((acc, date) => {
    return {
      ...acc,
      [date]: values[date] - averageDayValue,
    };
  }, {});

  // Собираем коллекцию квадратов отклонений

  const diffSquareList = valueADates.reduce<ValueList>((acc, date) => {
    acc[date] = diffList[date] ** 2;
    return acc;
  }, {});

  // Вычисляем сумму квадратов отклонений за все дни

  const diffSquareSum = Object.values(diffSquareList).reduce((acc, cur) => acc + cur, 0);

  // Вычисляем квадрат отклонений в день

  const diffSquarePerDay = diffSquareSum / valueDays;

  // Вычисляем среднее квадратичное отклонение

  const averageSquareDiff = Math.sqrt(diffSquarePerDay);

  // Вычисляем прогноз

  return sortedForecastDays.reduce<ForecastList>((acc, date, index) => {
    let min = averageDayValue - averageSquareDiff;
    let max = averageDayValue + averageSquareDiff;

    if (isCumulative) {
      // Дополнительная логика для кумулятивных графиков
      const daysCount = index + 1;
      min = min * daysCount + valueSum;
      max = max * daysCount + valueSum;
    }

    // average остаётся точечным прогнозом (среднее); нижнюю границу ПОЛОСЫ клампим в 0 — все
    // прогнозируемые величины неотрицательны (расход/выручка/клики/показы/конверсии), и при
    // stdDev > среднего `min` ушёл бы в минус. Отрицательная нижняя граница бессмысленна и для
    // additive-графика (полоса под нулём), и как вход для ratio-комбинирования.
    const average = (min + max) / 2;

    acc[date] = {
      average,
      range: [Math.max(0, min), max],
    };

    return acc;
  }, {});
};

export {calcForecast};
